import { Box, Heading, Stack, VStack } from '@chakra-ui/react'

import type { SeriesHomeSeriesData } from '@/common/services/seriesPage/types'

import { useAppContext } from '@/client/context/app'
import { PROGRAM_SLUG_TO_PROGRAM_ITERATIONS } from '@/client/services/programs/config'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { SAT_BOOTCAMP_WHAT_YOULL_DO } from '@/client/services/satBootcamp'

import useLayoutContext from '@/components/Layout/useLayoutContext'
import WhatYoullDo from '@/components/Programs/WhatYoullDo'
import SummerCampConfirmationModal from '@/components/SummerCamp/SummerCampConfirmationModal'
import RichText from '@/components/UI/RichTextEditor/RichText'
import TwoColumnContainer from '@/components/Worlds/UI/Container/TwoColumnContainer'

import SeriesHomeBanners from '../Banners/SeriesHomeBanners'
import SeriesDiscussion from '../SeriesDiscussion'
import SeriesStreakBar from '../SeriesStreakBar/SeriesStreakBar'
import SeriesHomeTasks from './SeriesHomeTasks'
import SeriesHomeTutoredBy from './SeriesHomeTutoredBy'
import SeriesNonParticipantSidebar from './SeriesNonParticipantSidebar'
import SeriesParticipantSidebar from './SeriesParticipantSidebar'
import Sessions from './Sessions/Sessions'

export default function SeriesHome({ series }: { series: SeriesHomeSeriesData }) {
  const { prepass } = useAppContext()
  const { navbarOffsetPx } = useLayoutContext()

  const isUserHost = series.viewer.isHost
  const isUserRegisteredLearner = series.viewer.isRegisteredLearner
  const isUserParticipant = isUserHost || isUserRegisteredLearner

  const description = series.description ?? EMPTY_RICH_TEXT_VALUE

  const worldSlug = series.world?.slug ?? ''

  const seriesTemplateSlug = series.seriesTemplateSlug ?? ''

  const allProgramOfferings = Object.entries(PROGRAM_SLUG_TO_PROGRAM_ITERATIONS).flatMap(
    ([programSlug, iterations]) =>
      iterations.flatMap(iteration =>
        iteration.offerings.map(offering => ({
          programSlug,
          offering,
        }))
      )
  )

  // Find the program offering that matches the series template of this series
  const { offering: matchingOffering } = allProgramOfferings.find(
    ({ offering: o }) => o.seriesTemplateSlug === seriesTemplateSlug
  ) ?? { offering: null }

  const whatYoullDo = series.isSATSeries
    ? SAT_BOOTCAMP_WHAT_YOULL_DO
    : (matchingOffering?.offeringTemplate?.whatYoullDo ?? null)

  return (
    <>
      <SummerCampConfirmationModal type="series" />
      <TwoColumnContainer
        primaryColumn={() => (
          <VStack spacing="32px" alignItems="stretch">
            <Stack direction={{ base: 'column-reverse', md: 'column' }} gap="32px">
              {isUserRegisteredLearner || prepass ? <SeriesStreakBar series={series} /> : null}
              <SeriesHomeBanners series={series} />
            </Stack>
            {!isUserParticipant || prepass ? (
              <>
                <VStack alignItems="stretch" gap="8px">
                  <Heading size="xl">About</Heading>
                  <Box padding={{ base: '12px', md: 'unset' }}>
                    <RichText content={description} />
                  </Box>
                </VStack>
                <SeriesHomeTutoredBy hosts={series.hosts} worldSlug={worldSlug} />
                {whatYoullDo != null ? (
                  <WhatYoullDo
                    titleSize="xl"
                    iconProps={whatYoullDo.iconProps}
                    bullets={whatYoullDo.bulletsLearner}
                  />
                ) : null}
              </>
            ) : null}
            {isUserParticipant || prepass ? (
              <SeriesHomeTasks world={series.world} isUserHost={isUserHost} />
            ) : null}
            <Sessions
              series={series}
              isUserParticipant={isUserParticipant}
              isUserRegisteredLearner={isUserRegisteredLearner}
            />
            <VStack alignItems="stretch" gap="16px">
              <Heading size="xl">Public Discussion</Heading>
              <SeriesDiscussion series={series} />
            </VStack>
          </VStack>
        )}
        secondaryColumn={() => (
          <Box position="sticky" top={`calc(${navbarOffsetPx} + 24px)`} height="fit-content">
            {isUserParticipant || prepass ? (
              <SeriesParticipantSidebar
                series={series}
                hosts={series.hosts}
                isUserRegisteredLearner={isUserRegisteredLearner}
              />
            ) : null}
            {!isUserParticipant || prepass ? <SeriesNonParticipantSidebar series={series} /> : null}
          </Box>
        )}
        secondaryColumnMobilePlacement={isUserParticipant ? 'tab' : 'top'}
        tabToLabel={{
          Primary: 'Home',
          Secondary: 'About',
        }}
      />
    </>
  )
}
